import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plug, Trash2 } from 'lucide-react';
import {
  createMcpIntegration,
  deleteMcpIntegration,
  listMcpIntegrations,
  patchMcpIntegration,
  testMcpIntegration,
  type McpIntegrationMeta,
} from '@/api/mcpIntegrations';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { notifications } from '@/utils/notifications';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

export const McpIntegrationsSettings = ({ embedded = false }: { embedded?: boolean }) => {
  const { t } = useTranslation('settings');
  const { hasFeature, loading: entsLoading } = useEntitlements();
  const { canManageAccount } = useAccountRole();
  const [rows, setRows] = useState<McpIntegrationMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<McpIntegrationMeta | null>(null);

  const canWriteByPlan = hasFeature('publicApiWrite');
  const canWrite = canWriteByPlan && canManageAccount;
  const readOnlyOrg = canWriteByPlan && !canManageAccount;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listMcpIntegrations());
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!name.trim() || !url.trim()) return;
    setCreating(true);
    try {
      await createMcpIntegration({ name: name.trim(), transport: 'http', url: url.trim() });
      setName('');
      setUrl('');
      notifications.success(t('mcpIntegrations.created'));
      await load();
    } catch {
      notifications.error(t('mcpIntegrations.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleTest = async (id: string) => {
    try {
      const { tools } = await testMcpIntegration(id);
      notifications.success(t('mcpIntegrations.testOk', { count: tools.length }));
    } catch {
      notifications.error(t('mcpIntegrations.testFailed'));
    }
  };

  if (entsLoading) return null;
  if (!canWriteByPlan) {
    return <p className="text-sm text-muted-foreground">{t('mcpIntegrations.upgradeHint')}</p>;
  }

  return (
    <div id={embedded ? 'mcp-servers' : undefined} className="space-y-4">
      {!embedded ? (
        <h2 className="text-lg font-semibold text-foreground">{t('mcpIntegrations.title')}</h2>
      ) : (
        <h3 className="text-base font-bold text-foreground">{t('mcpIntegrations.title')}</h3>
      )}
      <p className="text-sm text-muted-foreground">{t('mcpIntegrations.description')}</p>

      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}

      {canWrite ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            className="min-h-10 flex-1 rounded-lg border border-border bg-background px-3 text-sm"
            placeholder={t('mcpIntegrations.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="min-h-10 flex-[2] rounded-lg border border-border bg-background px-3 text-sm"
            placeholder={t('mcpIntegrations.urlPlaceholder')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            type="button"
            disabled={creating}
            onClick={() => void handleCreate()}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
            {t('mcpIntegrations.add')}
          </button>
        </div>
      ) : readOnlyOrg ? (
        <p className="text-xs text-muted-foreground">{t('mcpIntegrations.adminRequired')}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('mcpIntegrations.empty')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <span className="font-medium">{row.name}</span>
              <span className="text-xs text-muted-foreground">{row.transport}</span>
              {canWrite ? (
                <>
                  <label className="ml-auto inline-flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e) => void patchMcpIntegration(row.id, { enabled: e.target.checked }).then(load)}
                    />
                    {t('mcpIntegrations.enabled')}
                  </label>
                  <button type="button" className="text-xs font-semibold text-primary" onClick={() => void handleTest(row.id)}>
                    {t('mcpIntegrations.test')}
                  </button>
                  <button type="button" onClick={() => setDeleteTarget(row)} aria-label={t('mcpIntegrations.delete')}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </button>
                </>
              ) : (
                <span className="ml-auto text-xs text-muted-foreground">
                  {row.enabled ? t('mcpIntegrations.enabled') : t('mcpIntegrations.disabled')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={t('mcpIntegrations.deleteTitle')}
        confirmLabel={t('mcpIntegrations.delete')}
        isConfirming={false}
        onConfirm={() => {
          if (!deleteTarget) return;
          void deleteMcpIntegration(deleteTarget.id).then(() => {
            setDeleteTarget(null);
            void load();
          });
        }}
      >
        <p>{t('mcpIntegrations.deleteMessage', { name: deleteTarget?.name ?? '' })}</p>
      </ConfirmModal>
    </div>
  );
};
